import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchFirewallStatus,
  fetchMyIp,
  addRule,
  deleteRule,
  enableFirewall,
  disableFirewall,
  type FirewallStatus,
  type UfwRule,
  type AddRulePayload,
} from '../api/firewall';
import { usePermission } from '../hooks/usePermission';
import { getSocket } from '../api/socket';

// ── Helpers ────────────────────────────────────────────────────────────────

function getApiErrorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { code?: string } } } })
    ?.response?.data?.error?.code;
}

function getApiErrorMessage(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message;
}

function formatPort(rule: UfwRule): string {
  if (rule.to.port_range) return `${rule.to.port_range[0]}:${rule.to.port_range[1]}`;
  if (rule.to.port) return String(rule.to.port);
  return '*';
}

function formatFrom(rule: UfwRule): string {
  if (rule.from.type === 'anywhere') return 'Anywhere';
  return rule.from.value ?? '';
}

function ActionChip({ action }: { action: UfwRule['action'] }) {
  const map: Record<UfwRule['action'], { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    ALLOW:  { color: 'success', label: 'ALLOW' },
    DENY:   { color: 'error',   label: 'DENY' },
    REJECT: { color: 'error',   label: 'REJECT' },
    LIMIT:  { color: 'warning', label: 'LIMIT' },
  };
  const { color, label } = map[action];
  return <Chip label={label} color={color} size="small" />;
}

// ── Add Rule Dialog ────────────────────────────────────────────────────────

interface AddRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddRulePayload) => Promise<void>;
}

function AddRuleDialog({ open, onClose, onSubmit }: AddRuleDialogProps) {
  const [action, setAction]   = useState<AddRulePayload['action']>('allow');
  const [port, setPort]       = useState('');
  const [proto, setProto]     = useState<AddRulePayload['proto']>('tcp');
  const [from, setFrom]       = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleMyIp = async () => {
    try {
      const { ip } = await fetchMyIp();
      setFrom(ip);
    } catch {
      setError('Failed to fetch your IP');
    }
  };

  const handleSubmit = async () => {
    if (!port) { setError('Port is required'); return; }
    setError('');
    setLoading(true);
    try {
      await onSubmit({
        action,
        port,
        proto,
        from: from || undefined,
        comment: comment || undefined,
      });
      onClose();
      setPort(''); setFrom(''); setComment('');
    } catch {
      // error shown via global snackbar
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Firewall Rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl size="small" fullWidth>
            <InputLabel>Action</InputLabel>
            <Select value={action} label="Action" onChange={(e) => setAction(e.target.value as AddRulePayload['action'])}>
              <MenuItem value="allow">allow</MenuItem>
              <MenuItem value="deny">deny</MenuItem>
              <MenuItem value="reject">reject</MenuItem>
              <MenuItem value="limit">limit</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <TextField
              label="Port"
              size="small"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              placeholder="e.g. 8080"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Proto</InputLabel>
              <Select value={proto} label="Proto" onChange={(e) => setProto(e.target.value as AddRulePayload['proto'])}>
                <MenuItem value="tcp">tcp</MenuItem>
                <MenuItem value="udp">udp</MenuItem>
                <MenuItem value="any">any</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="From (optional)"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="IP or CIDR"
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="outlined" onClick={() => void handleMyIp()}>
              Use my IP
            </Button>
          </Stack>

          <TextField
            label="Comment (optional)"
            size="small"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 64 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Add Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Disable Firewall Dialog (typed confirm) ────────────────────────────────

interface DisableDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DISABLE_PHRASE = 'DISABLE FIREWALL';

function DisableDialog({ open, onClose, onConfirm }: DisableDialogProps) {
  const [text, setText]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
      setText('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>Disable Firewall</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Disabling the firewall exposes all ports. This is dangerous on a public server.
          </Alert>
          <Typography variant="body2">
            Type <strong>{DISABLE_PHRASE}</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={DISABLE_PHRASE}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={text !== DISABLE_PHRASE || loading}
          onClick={() => void handleConfirm()}
        >
          {loading ? <CircularProgress size={20} /> : 'Disable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function Firewall() {
  const canRead  = usePermission('firewall.read');
  const canWrite = usePermission('firewall.write');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<FirewallStatus>({
    queryKey: ['firewall'],
    queryFn: fetchFirewallStatus,
    enabled: canRead,
    refetchInterval: false,
  });

  const [addOpen, setAddOpen]               = useState(false);
  const [disableOpen, setDisableOpen]       = useState(false);
  const [actionLoading, setActionLoading]   = useState<string | null>(null);
  const [dockerEnvWrite, setDockerEnvWrite] = useState(false);
  const [enableError, setEnableError]       = useState<string | null>(null);

  const dockerEnv = data?.docker_env || dockerEnvWrite;

  // Live updates via socket
  useEffect(() => {
    if (!canRead) return;
    const socket = getSocket();

    const onStatus = (status: FirewallStatus) => {
      qc.setQueryData(['firewall'], status);
    };

    socket.on('firewall:status', onStatus);
    return () => { socket.off('firewall:status', onStatus); };
  }, [canRead, qc]);

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view the firewall.</Alert>;
  }

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">Failed to load firewall status.</Alert>;
  }

  const status = data!;

  const refresh = () => void qc.invalidateQueries({ queryKey: ['firewall'] });

  const handleToggle = async () => {
    if (status.active) {
      setDisableOpen(true);
    } else {
      setEnableError(null);
      setActionLoading('toggle');
      try {
        await enableFirewall();
        refresh();
      } catch (err) {
        const code = getApiErrorCode(err);
        if (code === 'firewall.docker_environment') setDockerEnvWrite(true);
        else if (code === 'firewall.unsafe_to_enable') {
          setEnableError(getApiErrorMessage(err) ?? 'Cannot enable firewall: add allow rules for ports 22, 80/443 first.');
        }
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleDisableConfirm = async () => {
    try {
      await disableFirewall();
      refresh();
    } catch (err) {
      if (getApiErrorCode(err) === 'firewall.docker_environment') setDockerEnvWrite(true);
      throw err;
    }
  };

  const handleDelete = async (rule: UfwRule) => {
    setActionLoading(`del-${rule.number}`);
    try {
      await deleteRule(rule.number);
      refresh();
    } catch (err) {
      if (getApiErrorCode(err) === 'firewall.docker_environment') setDockerEnvWrite(true);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddRule = async (payload: AddRulePayload) => {
    try {
      await addRule(payload);
      setEnableError(null);
      refresh();
    } catch (err) {
      if (getApiErrorCode(err) === 'firewall.docker_environment') setDockerEnvWrite(true);
      throw err;
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Firewall</Typography>
        <Stack direction="row" spacing={1}>
          {canWrite && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
              >
                Add Rule
              </Button>
              <Button
                variant={status.active ? 'outlined' : 'contained'}
                color={status.active ? 'error' : 'success'}
                disabled={actionLoading === 'toggle'}
                onClick={() => void handleToggle()}
              >
                {status.active ? 'Disable' : 'Enable'} Firewall
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Docker environment notice */}
      {dockerEnv && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          <strong>Docker environment detected</strong> — UFW cannot be managed from inside a Docker container.
          Configure firewall rules on the <strong>host machine</strong> using <code>ufw</code>,
          or use Docker's own port mapping (<code>-p</code> / <code>ports:</code> in Compose).
        </Alert>
      )}

      {/* Inactive notice — shown when UFW is off so user knows rules still work */}
      {!status.active && !dockerEnv && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Firewall is <strong>inactive</strong>. Rules shown below are pending and take effect when enabled.
          Make sure ports <strong>22 (SSH)</strong> and <strong>80 / 443 (HTTP/HTTPS)</strong> are allowed before enabling, or you will lose access to the server.
        </Alert>
      )}

      {/* Safety error from enable attempt */}
      {enableError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEnableError(null)}>
          {enableError}
        </Alert>
      )}

      {/* Status summary */}
      <Stack direction="row" spacing={2} mb={3} alignItems="center">
        <Chip
          label={status.active ? 'Active' : 'Inactive'}
          color={status.active ? 'success' : 'error'}
        />
        <Typography variant="body2" color="text.secondary">
          Default incoming: <strong>{status.default_incoming}</strong>
          {' · '}
          Default outgoing: <strong>{status.default_outgoing}</strong>
        </Typography>
      </Stack>

      {/* Rules table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Port / Proto</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {status.rules.map((rule) => (
              <TableRow key={`${rule.number}-${rule.ipv6 ? 'v6' : 'v4'}`} hover>
                <TableCell>{rule.number}</TableCell>
                <TableCell><ActionChip action={rule.action} /></TableCell>
                <TableCell>
                  <Chip label={rule.direction} size="small" variant="outlined" />
                  {rule.ipv6 && <Chip label="v6" size="small" variant="outlined" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell>
                  {formatPort(rule)}
                  {rule.to.protocol && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}/{rule.to.protocol}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatFrom(rule)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {rule.comment && (
                      <Typography variant="body2" color={rule.managed ? 'primary.main' : 'text.primary'}>
                        {rule.comment}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {rule.protected ? (
                    <Tooltip title="Protected — cannot be deleted">
                      <LockIcon fontSize="small" color="disabled" />
                    </Tooltip>
                  ) : canWrite ? (
                    <Tooltip title="Delete rule">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={actionLoading === `del-${rule.number}`}
                          onClick={() => void handleDelete(rule)}
                        >
                          {actionLoading === `del-${rule.number}` ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {status.rules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No rules configured
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddRuleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAddRule}
      />
      <DisableDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onConfirm={handleDisableConfirm}
      />
    </Box>
  );
}
